import smoothscroll from "smoothscroll-polyfill";
import { Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { useEffect, useRef, useState } from "react";
import {
  NO_VARIANT_CODE,
  SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
  WARNING_OR_ERROR_EVENT,
} from "../../../../constants/constants";
import styles from "./SizeError.module.scss";

type SizeErrorProps = {
  sizes: {
    name: string | null | undefined;
  }[];
};

export const SizeError = ({ sizes }: SizeErrorProps) => {
  const [hasSizeError, setHasSizeError] = useState(false);
  const errorMessageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => setHasSizeError(false), [sizes]);
  useEffect(() => {
    const handleNoSizeSelectedError = () => {
      setHasSizeError(true);
    };

    const errorOrWarningHandler = (
      event: CustomEvent<{ errors: { code: string }[] }>,
    ) => {
      const data = event.detail;
      const { errors = [] } = data;
      const hasVariantError = errors.some(
        ({ code }) => code === NO_VARIANT_CODE,
      );

      if (hasVariantError) {
        setHasSizeError(true);
      }
    };

    window.addEventListener(
      SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
      handleNoSizeSelectedError,
    );

    window.addEventListener(WARNING_OR_ERROR_EVENT, errorOrWarningHandler);

    return () => {
      window.removeEventListener(
        SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
        handleNoSizeSelectedError,
      );
      window.removeEventListener(WARNING_OR_ERROR_EVENT, errorOrWarningHandler);
    };
  }, []);

  useEffect(() => {
    if (hasSizeError) {
      smoothscroll.polyfill();
      const sizeErrorElement = errorMessageRef.current;
      if (sizeErrorElement) {
        const headerOffset = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--jl-header-height",
          ),
        );
        const elementPosition = sizeErrorElement.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }
  }, [hasSizeError]);

  if (sizes.length == 0) {
    return null;
  }

  return (
    <>
      {hasSizeError && (
        <div id="size:error" data-testid="size:error" ref={errorMessageRef}>
          <Message
            className={styles.message}
            type="error"
            title="Select a size"
          />
        </div>
      )}
    </>
  );
};
